// Single source of truth for the navigation IA: drawer groups + bottom dock.
// Pages are filtered by `hasViewAccess` at render time so no role logic here.

import {
  Buildings, Package, ClipboardText, Scissors, Swatches, CheckCircle,
  ChartBar, Factory, Users, ListChecks, CurrencyInr, Receipt, Book,
  WarningCircle, Headset, CreditCard, Crown, Gear, Dress,
  Palette, Tag, BellRinging, FileMagnifyingGlass, IdentificationCard,
  GraduationCap, Coins, FileText, Lightning, Cube,
} from '@phosphor-icons/react';

// 7 mobile dock primary departments → action sheet contents
// Order follows the real garment industry workflow:
//   Sampling → Costing → PO → Cutting → Production → Packing → Finance
export const DOCK = [
  {
    key: 'sampling',
    label: 'Sampling',
    icon: Dress,
    primaryPath: '/sampling',
    sheet: [
      { path: '/sampling', label: 'Sampling Planner', icon: Dress, desc: 'Track samples in development' },
    ],
  },
  {
    key: 'costing',
    label: 'Costing',
    icon: Coins,
    sheet: [
      { path: '/costings',         label: 'Product Costings',  icon: FileText, desc: 'Style-wise cost & quotation' },
      { path: '/costings/reports', label: 'Costing Reports',   icon: ChartBar, desc: 'Margins · pending · top spend' },
      { path: '/cost-master',      label: 'Cost Master',       icon: Coins,    desc: 'Materials, labour & vendor rates' },
    ],
  },
  {
    key: 'materials',
    label: 'Materials',
    icon: Cube,
    primaryPath: '/materials',
    sheet: [
      { path: '/materials',             label: 'Stock & Inventory',    icon: Package,  desc: 'Stock inward · inventory ledger'          },
      { path: '/materials?tab=demand',  label: 'Demand & Fulfillment', icon: Lightning, desc: 'FIFO fulfillment & requirement reports'  },
      { path: '/materials?tab=master',  label: 'Material Master',       icon: Coins,    desc: 'Cost rates, vendors & variant overrides'  },
    ],
  },
  {
    key: 'po',
    label: 'PO',
    icon: ClipboardText,
    sheet: [
      { path: '/master-styles',    label: 'Master Styles',         icon: Swatches,      desc: 'Style catalogue & SKUs' },
      { path: '/purchase-orders',  label: 'Purchase Orders',       icon: ClipboardText, desc: 'PO list & demand' },
      { path: '/po-completion',    label: 'PO Completion Center',  icon: CheckCircle,   desc: 'Closed-PO review & P&L' },
      // iter164d — Live Tracking promoted from a tab into a first-class
      // nav item so users can jump straight to real-time PO status from
      // any page. Green tone matches the tab-strip colour used inside
      // the PO page for visual continuity.
      { path: '/purchase-orders?tab=tracking', label: 'Live Order Tracking', icon: Lightning, desc: 'Real-time PO status', tone: 'emerald' },
    ],
  },
  {
    key: 'cutting',
    label: 'Cutting',
    icon: Scissors,
    sheet: [
      { path: '/cutting/pick-style', label: 'Pick a Style',     icon: Scissors,    desc: 'Plan from pending demand' },
      { path: '/cutting/tables',     label: 'Tables Setup',     icon: Swatches,    desc: 'Allocate styles to tables' },
      { path: '/cutting/done',       label: 'Cutting Done',     icon: CheckCircle, desc: 'Record actual cuts' },
      { path: '/cutting/analysis',   label: 'Cutting Analysis', icon: ChartBar,    desc: 'Planned vs actual reports' },
    ],
  },
  {
    key: 'production',
    label: 'Production',
    icon: Factory,
    sheet: [
      { path: '/contractors',   label: 'Contractors',       icon: Users,       desc: 'Stitching partners' },
      { path: '/rate-master',   label: 'Rate Master',       icon: CurrencyInr, desc: 'Brand-wise stitching rates' },
      { path: '/lots',          label: 'Lots & Tracking',   icon: Package,     desc: 'Track lots in production' },
      { path: '/production',    label: 'Production Status', icon: ListChecks,  desc: 'Live floor status' },
    ],
  },
  {
    key: 'packing',
    label: 'Packing',
    icon: Package,
    primaryPath: '/packing',
    sheet: [
      { path: '/packing', label: 'Packing & Stock', icon: Package, desc: 'Barcode → Stock → Ship' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: CurrencyInr,
    sheet: [
      { path: '/invoices', label: 'Contract Invoices', icon: Receipt, desc: 'Generate stitching invoices' },
      { path: '/ledgers',  label: 'Contractor Ledgers', icon: Book,    desc: 'Payments & balances' },
    ],
  },
];

// Side drawer groups (admin / utility — secondary stuff not on dock).
// Per spec: NO Alerts, NO Support — both already live in the top bar.
export const DRAWER_GROUPS = [
  {
    label: 'Workspace',
    items: [
      { path: '/settings?tab=users',         label: 'Users',           icon: Users,                desc: 'Team members & permissions' },
      { path: '/settings?tab=company',       label: 'Company Details', icon: IdentificationCard,   desc: 'Branding, address, GST' },
      { path: '/settings?tab=factories',     label: 'Factories',       icon: Buildings,            desc: 'Production units' },
      { path: '/settings?tab=brands',        label: 'Brands',          icon: Tag,                  desc: 'Client brands & code prefixes' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { path: '/settings?tab=presets',       label: 'Master Data',     icon: Palette,              desc: 'Colors, sizes, categories' },
      { path: '/settings?tab=notifications', label: 'Notifications',   icon: BellRinging,          desc: 'Email, push & WhatsApp setup' },
      { path: '/settings?tab=audit',         label: 'Audit Log',       icon: FileMagnifyingGlass,  desc: 'Every action by every user' },
    ],
  },
  {
    label: 'Account',
    items: [
      { path: '/training',    label: 'Training Hub',     icon: GraduationCap, desc: 'Watch tutorials & onboarding videos' },
      { path: '/billing',     label: 'Plans & Billing',  icon: CreditCard, desc: 'Subscription & invoices', ownerOnly: true },
      { path: '/super-admin', label: 'Platform Console', icon: Crown,      desc: 'Multi-tenant SaaS admin', superAdminOnly: true },
    ],
  },
];

export const QUICK_BAR_HOME = '/dashboard';
